/**
 * @description
 * LoginComponent handles the user authentication flow.
 * It is called via the `/login` route.
 *
 * Dependencies:
 * - LoginService (handles authentication requests and session state)
 * - CommonService (fetches store and station metadata after authentication)
 * - ServerStatusService (upfront server connectivity check)
 * - AlertService (displays success or error notifications to the user)
 * - SelectStationComponent (sub-component for station selection once logged in)
 * - MenuCommonComponent (renders common header/navigation layout)
 * - OfflineBannerComponent (shared banner displayed when server is unreachable)
 */

import { Component, DestroyRef, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

import { forkJoin, throwError } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { LoginService } from '@app/common/login.service';
import { CommonService } from '@app/common/common.service';
import { ServerStatusService } from '@app/common/server-status.service';
import { AlertService } from '@app/shared/alert.service';
import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { OfflineBannerComponent } from '@app/shared/offline-banner/offline-banner.component';
import { Station, Store } from '@app/shared/_models/common';
import { StationSelectionService } from '@app/shared/select-station/station-selection.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MenuCommonComponent,
    OfflineBannerComponent
],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class LoginComponent implements OnInit {
  /** Form group instance for the login input controls. */
  form!: FormGroup;
  /** State indicator representing whether the login submission is currently in progress. */
  loading = false;
  /** Flag showing if the login form has been submitted at least once. */
  submitted = false;
  /** State indicating whether the user is currently successfully authenticated. */
  logged = false;

  /** List of stations available to the logged-in user. */
  stations?: Station[];
  /** List of stores available to the logged-in user. */
  stores?: Store[];

  private readonly formBuilder = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly alertService = inject(AlertService);
  private readonly serverStatusService = inject(ServerStatusService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly loginService = inject(LoginService);
  public readonly commonService = inject(CommonService);
  private readonly stationSelectionService = inject(StationSelectionService);

  ngOnInit(): void {
    this.serverStatusService.checkConnection();
    this.loginService.logout();

    this.form = this.formBuilder.group({
      username: ['jp', Validators.required],
      password: ['jp', Validators.required],
    });
  }

  /**
   * Submits the login credentials to the server.
   * On success, sets the session and loads stores/stations via forkJoin.
   * On failure, displays error feedback via AlertService.
   */
  onSubmit(): void {
    this.submitted = true;
    this.alertService.clear();

    if (this.form.invalid) {
      return;
    }

    this.loading = true;
    this.loginService.logout();
    this.loginService.login(this.f['username'].value, this.f['password'].value)
      .pipe(
        switchMap((rep) => {
          if (rep && !rep.error_code) {
            this.logged = true;
            return forkJoin({
              stations: this.commonService.getStations(),
              stores: this.commonService.getStores(),
            });
          }
          return throwError(() => new Error(rep?.error_msg || "Échec de l'authentification"));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (res) => {
          this.stations = res.stations;
          this.stores = res.stores;
          this.stationSelectionService.prompt(
            () => {
              this.loading = false;
              this.stationSelected();
            },
            () => this.logout()
          );
        },
        error: (error: Error) => {
          this.loading = false;
          console.error('LoginComponent ERROR:', error.message);
          this.alertService.error(error.message || String(error));
        },
      });
  }

  /**
   * Handles the action when a station is selected.
   * Navigates the user to the redirect URL or fallback homepage.
   */
  stationSelected(): void {
    if (this.loginService.currentstore !== undefined && this.loginService.currentstation !== undefined && this.loginService.loggeduser) {
      const returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
      this.router.navigateByUrl(returnUrl);
    }
  }

  /**
   * Logs the user out by clearing session state and resetting local component values.
   */
  logout(): void {
    this.loginService.logout();
    this.logged = false;
    this.submitted = false;
    this.alertService.clear();
  }

  /** Convenience getter for easy access to form fields. */
  get f() { return this.form.controls; }
}
